import { hasConfirmedOperation, executionDecision, selectedCommandStatus, chooseExecution, tonnageStatus } from "./consolidationExecution.js";
import { localDateTime, localTimeCandidates, shiftDate } from "./consolidationPresentation.js";
import { fillFromGeotab } from "./consolidationGeotabFill.js";
import { orderByDeparture } from "./consolidationVisitOrder.js";

const empty = (value) => value == null || value === "";
function put(object, [key, ...rest], value) {
  const copy = Array.isArray(object) ? [...object] : { ...object };
  if (rest.length) copy[key] = put(object?.[key], rest, value);
  else if (value === undefined) delete copy[key];
  else copy[key] = value;
  return copy;
}
const validTime = (value) => typeof value === "string" && /(?:Z|[+-]\d{2}:\d{2})$/.test(value) && Number.isFinite(Date.parse(value));
const validTons = (value) => !empty(value) && Number.isFinite(Number(value)) && Number(value) >= 0;
// The Dispatch may serialize an otherwise ordinary value as "1E+1". Keep
// number inputs readable when copying it into a real tonnage field.
const inputTons = (value) => empty(value) ? value : String(Number(value));

// Complète seulement les quantités ; le tonnage livré rempli dit le statut, comme une saisie.
function fillSheetQuantities(row, quantities, include = () => true) {
  const result = { row, quantities, missing: 0 };
  for (const visit of row.visits) {
    if (visit.status === "inherited" || visit.pm?.role === "source" || !include(visit)) continue;
    const field = visit.kind === "loading" ? "loaded_tons" : "delivered_tons";
    for (const order of visit.orders) {
      if (selectedCommandStatus(order, executionDecision(row, order)) === "excluded" || order.separate_treatment) continue;
      if (visit.kind === "delivery" && order.delivery_count !== 1) continue;
      if (!empty(result.quantities[order.id]?.[field] ?? order[field])) continue;
      if (!validTons(order.planned_tons)) { result.missing += 1; continue; }
      result.quantities = put(result.quantities, [order.id, field], inputTons(order.planned_tons));
      if (visit.kind === "delivery") result.row = chooseExecution(result.row, order, tonnageStatus(order.planned_tons));
    }
  }
  return result;
}

export function fillSheetFromGeotab(row, quantities, truck) {
  if (!truck?.comparison) throw new Error(truck ? "Geotab : aucun trajet pour ce camion ce jour-là." : "Geotab : camion introuvable.");
  const real = fillFromGeotab(row, truck, { preserveExclusions: true });
  if (!real.filled && !real.service) throw new Error("Geotab : aucun arrêt ne correspond à ce relevé.");
  // Seules les livraisons vues par Geotab reçoivent leur tonnage prévu, qui vaut Livrée ; les autres restent à saisir.
  const filled = fillSheetQuantities(real.row, quantities, visit => visit.kind !== "delivery" || visit.geotab);
  const quantity = (order, field) => filled.quantities[order.id]?.[field] ?? order[field] ?? "";
  return { row: { ...filled.row, visits: orderByDeparture(filled.row, quantity) }, quantities: filled.quantities,
    missing: real.missing + filled.missing };
}

export function serviceInstant(value, row, plannedStart = null) {
  if (validTime(value)) return value;
  if (empty(value) || !row.timezone) return null;
  const clockOnly = /^\d{2}:\d{2}(?::\d{2})?$/.test(value);
  let day = row.dispatch_date;
  if (clockOnly && plannedStart) {
    const start = localDateTime(plannedStart, row.timezone);
    day = start.slice(0, 10);
    if (value.slice(0, 5) < start.slice(11, 16)) day = shiftDate(day, 1);
  }
  const candidates = localTimeCandidates(clockOnly ? `${day}T${value}` : value, row.timezone);
  return candidates.length === 1 ? candidates[0].instant : null;
}

export function clearDispatchValues(row, quantities, preload = false) {
  const result = { row: { ...row }, quantities: { ...quantities } };
  if (preload) {
    result.row.start_at = null;
    result.row.end_at = null;
    for (const order of row.links) result.quantities[order.order_id] = "";
  } else {
    result.row.service_start = null;
    result.row.service_end = null;
    result.row.truck_departure = null;
    result.row.lunch_start = null;
    result.row.lunch_end = null;
    result.row.no_lunch = true;
    result.row.breaks = [];
    result.row.execution_decisions = (row.execution_decisions || []).filter(d => d.status === "not_delivered");
    const isExcluded = order => selectedCommandStatus(order, executionDecision(row, order)) === "excluded";
    result.row.visits = row.visits.map((visit) => {
      if (visit.status === "inherited" || (visit.pm?.role !== "source" && visit.orders.every(isExcluded))) return visit;
      const field = visit.kind === "loading" ? "loaded_tons" : "delivered_tons";
      for (const order of visit.orders) {
        if (visit.pm?.role === "source") continue;
        if (isExcluded(order)) continue;
        if (visit.kind === "delivery" && order.delivery_count !== 1) continue;
        // An explicit empty override hides persisted quantities and saves as null.
        result.quantities[order.id] = { ...result.quantities[order.id], [field]: "" };
      }
      return { ...visit, arrival: null, departure: null, geotab: false, status: hasConfirmedOperation(visit) ? "performed" : "unset" };
    });
  }
  return result;
}
